import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import type { SeoNavbarLink } from '@freelancer/datastore/collections';
import { SeoNavbarParent } from '@freelancer/datastore/collections';
import { Location } from '@freelancer/location';
import {
  CalloutPlacement,
  NAVIGATION_OWNED_VIEWPORT_FIT,
} from '@freelancer/ui/callout';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { QueryParams } from '@freelancer/ui/link';

@Component({
  standalone: false,
  selector: 'app-mega-menu',
  template: `
    <fl-callout
      [edgeToEdge]="true"
      [placement]="CalloutPlacement.BOTTOM_LEFT"
      [hideCloseButton]="true"
      [hover]="true"
      [mobileFullscreen]="true"
      (calloutOpen)="openCalloutHandler()"
      (calloutClose)="closeCalloutHandler()"
      [mobileHeaderTitle]="treeData.name"
      [crawlable]="crawlable"
      [viewportFit]="NAVIGATION_OWNED_VIEWPORT_FIT"
    >
      <fl-callout-trigger>
        <a
          flTrackingSection="SeoNavBarMegaMenu"
          flTrackingLabel="SeoNavBarMegaMenuRoot"
          [href]="treeData.relativeUrl"
          (click)="handleRootLink()"
        >
          <div
            (mouseenter)="openCalloutHandler()"
            (mouseleave)="closeCalloutHandler()"
          >
            <p class="text-light">
              {{ treeData.name }}
            </p>
          </div>
        </a>
      </fl-callout-trigger>

      <fl-callout-content>
        <div
          class="Container"
          flTrackingSection="SeoNavBarMegaMenu"
          flTrackingLabel="SeoNavBarMegaMenuContainer"
          tabindex="-1"
          (mouseenter)="openCalloutHandler()"
          (click)="onClick($event)"
        >
          @for (
            column of columns;
            track trackByDepth(depth, column);
            let depth = $index
          ) {
            <div
              class="Container-innerContainer"
              [class.Container-border]="isContainerBorderVisible(depth)"
              [class.MobileNextColumn]="
                (!lastKnowActiveNode && depth > 0) ||
                lastKnowActiveNode?.depth + 2 <= depth
              "
              [class.MobileCurrColumn]="
                (!lastKnowActiveNode && depth === 0) ||
                (lastKnowActiveNode && lastKnowActiveNode?.depth + 1 == depth)
              "
              [class.MobilePrevColumn]="
                lastKnowActiveNode && lastKnowActiveNode?.depth >= depth
              "
            >
              @if (column.depth > 0) {
                <div
                  class="Item Item--active Item--cursor mb-small tablet:mb-xxsmall mobile:show"
                  [flTrackingLabel]="'SeoNavbarBack'"
                  tabindex="-1"
                  (click)="setVisible(lastKnowActiveNode?.parent, true)"
                >
                  <fl-icon
                    class="Item-previousIndicator"
                    [name]="'ui-chevron-left'"
                    [color]="IconColor.INHERIT"
                    [size]="IconSize.SMALL"
                    [useIconFont]="true"
                    [fillIconFont]="true"
                  ></fl-icon>
                  <p class="text-color-inherit mr-mid">
                    {{ lastKnowActiveNode?.name }}
                  </p>
                </div>
              }
              @for (
                parent of column.parentNodes;
                track trackByName($index, parent)
              ) {
                <div
                  class="ChildNodesContainer"
                  [class.ChildNodesContainer--visible]="parent.active"
                >
                  @for (
                    nodeItem of parent.nodes;
                    track trackByName($index, nodeItem);
                    let isLast = $last
                  ) {
                    @if (nodeItem.isParent) {
                      <div
                        class="Item Item--cursor {{
                          isLast ? 'mb-none' : 'mb-small'
                        }} {{
                          isLast ? 'tablet:mb-none' : 'tablet:mb-xxsmall'
                        }}"
                        [class.Item--active]="nodeItem.active"
                        [flTrackingLabel]="'SeoNavBar ' + nodeItem.name"
                        tabindex="-1"
                        (mouseenter)="setVisible(nodeItem)"
                        (click)="setVisible(nodeItem)"
                      >
                        <p class="text-color-inherit mr-mid">
                          {{ nodeItem.name }}
                        </p>
                        <fl-icon
                          [name]="'ui-chevron-right'"
                          [color]="IconColor.INHERIT"
                          [size]="IconSize.SMALL"
                          [useIconFont]="true"
                          [fillIconFont]="true"
                        ></fl-icon>
                      </div>
                    }
                    @if (!nodeItem.isParent) {
                      <div
                        class="Item {{ isLast ? 'mb-none' : 'mb-small' }} {{
                          isLast ? 'tablet:mb-none' : 'tablet:mb-xxsmall'
                        }}"
                      >
                        <a
                          class="Item-link"
                          [flTrackingLabel]="'SeoNavBar ' + nodeItem.name"
                          [href]="nodeItem.relativeUrl"
                        >
                          {{ nodeItem.name }}
                        </a>
                      </div>
                    }
                  }
                </div>
              }
            </div>
          }
        </div>
      </fl-callout-content>
    </fl-callout>
  `,
  styleUrls: ['./mega-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MegaMenuComponent implements OnChanges {
  private location = inject(Location);
  private router = inject(Router);

  CalloutPlacement = CalloutPlacement;
  NAVIGATION_OWNED_VIEWPORT_FIT = NAVIGATION_OWNED_VIEWPORT_FIT;
  IconColor = IconColor;
  IconSize = IconSize;

  lastKnowActiveNode?: MenuNode;
  columns: readonly Column[];

  @Input() crawlable: boolean;
  @Input() treeData: SeoNavbarParent;
  @Output() isActiveTrigger = new EventEmitter<boolean>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('treeData' in changes && this.treeData) {
      this.columns = [];
      this.arrangeByDepth();
    }
  }

  isSeoNavbarParent(
    result: SeoNavbarParent | SeoNavbarLink,
  ): result is SeoNavbarParent {
    return (result as SeoNavbarParent).nodes !== undefined;
  }

  convertToMenuMode(
    node: SeoNavbarParent | SeoNavbarLink,
    depth: number,
    parentMenu?: MenuNode,
  ): MenuNode {
    let childNodes: MenuNode[] = [];
    const newMenuNode: MenuNode = {
      name: node.name,
      relativeUrl: node.relativeUrl
        ? new URL(`${this.location.origin}${node.relativeUrl}`).toString()
        : undefined,
      parent: parentMenu,
      active: false,
      depth,
      isParent: this.isSeoNavbarParent(node),
    };

    if (this.isSeoNavbarParent(node)) {
      childNodes = node.nodes.map((value, index) =>
        this.convertToMenuMode(value, depth + 1, newMenuNode),
      );
    }
    newMenuNode.nodes = childNodes;

    return newMenuNode;
  }

  arrangeByDepth(): void {
    const topMostColumns = this.treeData.nodes.map(node =>
      this.convertToMenuMode(node, 0),
    ) as readonly MenuNode[];
    let parentNodes: readonly MenuNode[] = [
      {
        name: this.treeData.name,
        nodes: topMostColumns,
        active: true,
        depth: 0,
        isParent: true,
      },
    ];
    let depth = 0;
    while (parentNodes.length > 0) {
      this.columns = [
        ...this.columns,
        {
          depth,
          parentNodes,
        },
      ];

      parentNodes = this.columns[depth].parentNodes.reduce(
        (allNodes, parent) => [
          ...allNodes,
          ...(parent.nodes ? parent.nodes : []),
        ],
        [] as readonly MenuNode[],
      );
      depth++;
    }
  }

  trackByDepth(_: number, column: Column): number {
    return column.depth;
  }

  trackByName(_: number, node: MenuNode): string {
    return node.parent ? node.parent.name + node.name : node.name;
  }

  setVisible(node?: MenuNode, back = false): void {
    if (back || (node && node.nodes && node.nodes.length > 0)) {
      this.deactivateLastKnowActive();
      let currentNode: MenuNode | undefined = node;
      while (currentNode) {
        currentNode.active = true;
        currentNode = currentNode.parent;
      }
      this.lastKnowActiveNode = node;
    }
  }
  openCalloutHandler(): void {
    this.isActiveTrigger.emit(true);
  }
  closeCalloutHandler(): void {
    this.isActiveTrigger.emit(false);
    this.deactivateLastKnowActive();
  }

  deactivateLastKnowActive(): void {
    let currentNode: MenuNode | undefined = this.lastKnowActiveNode;
    while (currentNode) {
      currentNode.active = false;
      currentNode = currentNode.parent;
    }
    this.lastKnowActiveNode = undefined;
  }

  onClick(event: Event): void {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const link = target.getAttribute('href');

    if (link) {
      const params: QueryParams = {};
      const url = new URL(link);
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      this.location.navigateByUrl(
        this.router.createUrlTree([url.pathname], {
          queryParams: params,
        }),
      );
    }
  }

  handleRootLink(): boolean {
    // Prevent navigation by root level link on touch devices
    // so sub-menus are displayed

    /* eslint-disable no-restricted-globals */
    const isNotTouch =
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !('ontouchstart' in window || navigator.maxTouchPoints > 0);

    return isNotTouch;
  }

  isContainerBorderVisible(depth: number): boolean {
    if (!this.lastKnowActiveNode || depth <= 0) {
      return false;
    }

    const isWithinActiveDepth = depth <= this.lastKnowActiveNode.depth;
    const isNextDepthWithChildren =
      depth === this.lastKnowActiveNode.depth + 1 &&
      (this.lastKnowActiveNode.nodes?.length ?? 0) > 0;

    return isWithinActiveDepth || isNextDepthWithChildren;
  }
}

interface Column {
  depth: number;
  parentNodes: readonly MenuNode[];
}

interface MenuNode {
  name: string;
  nodes?: readonly MenuNode[];
  relativeUrl?: string;
  parent?: MenuNode;
  active: boolean;
  depth: number;
  isParent: boolean;
}
